import { createContext, useCallback, useContext, useState } from 'react';
import { DialogTrigger } from 'react-aria-components';
import Hamburger from '@/assets/icons/Hamburger.svg';
import { IconButton } from '@/components/IconButton';
import type { NavLinkProps, NavSubmenuLinkProps } from '@/types';
import { checkHasSubmenu } from './checkHasSubmenu';
import { MenuHeader } from './MenuHeader';
import { ProductLinks } from './ProductLinks';
import { Submenu } from './Submenu';
import { UtilityLinks } from './UtilityLinks';
import * as Styled from './MenuToggle.styled';

interface MenuToggleProps {
  productLinks: NavLinkProps[];
  utilityLinks: NavSubmenuLinkProps[];
}

const MenuContext = createContext<(() => void) | null>(null);

export const MenuToggle = ({ productLinks, utilityLinks }: MenuToggleProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const [submenuData, setSubmenuData] = useState<NavLinkProps | null>(null);

  const toggleMenu = useCallback(() => {
    setIsOpen(currentOpenState => !currentOpenState);
    setIsSubmenuOpen(false);
    setSubmenuData(null);
  }, []);

  return (
    <MenuContext.Provider value={toggleMenu}>
      <DialogTrigger isOpen={isOpen} onOpenChange={toggleMenu}>
        <IconButton icon={<Hamburger />} label="Menu" width={24} />
        <Styled.Underlay>
          <Styled.Overlay>
            <Styled.Menu>
              <MenuHeader />
              {isSubmenuOpen && submenuData && checkHasSubmenu(submenuData) ? (
                <Submenu
                  data={submenuData}
                  setIsOpen={setIsOpen}
                  setIsSubmenuOpen={setIsSubmenuOpen}
                  setSubmenuData={setSubmenuData}
                />
              ) : (
                <>
                  <ProductLinks
                    links={productLinks}
                    setIsOpen={setIsOpen}
                    setIsSubmenuOpen={setIsSubmenuOpen}
                    setSubmenuData={setSubmenuData}
                  />
                  <UtilityLinks links={utilityLinks} />
                </>
              )}
            </Styled.Menu>
          </Styled.Overlay>
        </Styled.Underlay>
      </DialogTrigger>
    </MenuContext.Provider>
  );
};

export const useCloseMenu = () => {
  const context = useContext(MenuContext);

  if (context === undefined || context === null) {
    throw new Error('useCloseMenu must be used within an MenuContext Provider');
  }

  return context;
};
